import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@mui/material";
import GenericAlert from "../GenericAlert";
import {
  AUTHENTICATION_PATH,
  DEFAULT_COUNTRY,
  ERROR,
  GET,
  PUT,
} from "../../utils/constants";
import { validateInputs } from "../../utils/inputValidations";
import {
  IBillingFormErrors,
  IBillingFormStep,
  ICompleteRegistrationSubmit,
  IEntityCountryResponse,
  ISignupClientPt2,
  IValidateToken,
} from "../../interfaces/signup";
import { ITargetValue } from "../../interfaces/form";
import { IAlert } from "../../interfaces/alert";
import { fetchData } from "../../utils/dataProvider";
import GenericForm from "../GenericForm";
import { trimValues } from "../../utils/form";
import DynamicBillingFields from "./DynamicBillingFields";
import { countryStrategyFactory } from "../../strategies/countryStrategyFactory";

const getErrorMessage = (error: unknown): string => {
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  return String(error);
};

const completeClientRegistration = async ({
  selectedValues,
  navigate,
  setAlert,
  token,
}: ICompleteRegistrationSubmit) => {
  try {
    await fetchData({
      url: `/clients/confirm/${token}`,
      method: PUT,
      body: trimValues(selectedValues),
    });
    navigate(`${AUTHENTICATION_PATH}?reason=signup`);
  } catch (error: unknown) {
    setAlert({
      typeOfAlert: ERROR,
      message: getErrorMessage(error),
      showAlert: true,
    });
  }
};

const validateToken = async ({
  token,
  setAlert,
  setInvalidToken,
}: IValidateToken) => {
  try {
    await fetchData({
      url: `/clients/validateToken/${token}`,
      method: GET,
    });
  } catch (error: unknown) {
    setAlert({
      typeOfAlert: ERROR,
      message: getErrorMessage(error),
      showAlert: true,
    });
    setInvalidToken(true);
  }
};

const BillingForm = ({
  selectedValues,
  setSelectedValues,
  setGridSize,
}: IBillingFormStep) => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [errors, setErrors] = useState({} as IBillingFormErrors);
  const [alert, setAlert] = useState({} as IAlert);
  const [invalidToken, setInvalidToken] = useState(false);
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const strategy = countryStrategyFactory.getStrategy(country);

  const handleChange = ({ name, value }: ITargetValue) =>
    setSelectedValues({
      ...selectedValues,
      [name]: value,
    });

  const handleSubmit = () => {
    const formValidations = strategy.getValidations();
    const inputErrors = validateInputs(selectedValues, formValidations);
    setErrors(inputErrors);
    if (Object.keys(inputErrors).length === 0) {
      const submitData = strategy.transformSubmitData
        ? (strategy.transformSubmitData(
          selectedValues
        ) as unknown as ISignupClientPt2)
        : selectedValues;
      completeClientRegistration({
        selectedValues: { ...submitData, country },
        navigate,
        setAlert,
        token,
      });
    }
  };

  const fetchEntityCountry = async () => {
    try {
      const response: IEntityCountryResponse = await fetchData({
        url: `/entities/country/client/${token}`,
        method: GET,
      });
      setCountry(response.country);
      setSelectedValues({ ...selectedValues, country: response.country });
    } catch {
      setCountry(DEFAULT_COUNTRY);
      setSelectedValues({ ...selectedValues, country: DEFAULT_COUNTRY });
    }
  };

  useEffect(() => {
    setGridSize(4);
    validateToken({ token, setAlert, setInvalidToken });
    fetchEntityCountry();
  }, []);
  return (
    <>
      {alert.showAlert && <GenericAlert alert={alert} />}
      <GenericForm onSubmit={handleSubmit}>
        <DynamicBillingFields
          fields={strategy.fields}
          values={selectedValues as unknown as Record<string, unknown>}
          errors={errors as unknown as Record<string, string[]>}
          onChange={handleChange}
        />
      </GenericForm>
      <Button
        variant="contained"
        className="m-t-40 w-100"
        onClick={handleSubmit}
        disabled={invalidToken}
      >
        Continuar
      </Button>
    </>
  );
};

export default BillingForm;
